import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { useLang } from "../../context/LangContext";
import { c } from "../../content";

function Principle({ item, delay, right }) {
  return (
    <div
      className={`reveal d${delay}${right ? " principle-right" : ""}`}
      style={{ display: "flex", flexDirection: "column" }}
    >
      <span
        aria-hidden="true"
        style={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: "0.58rem",
          letterSpacing: "0.38em",
          color: "rgba(200,121,65,0.42)",
          textTransform: "uppercase",
          marginBottom: "2rem",
          display: "block",
        }}
      >
        {item.num}
      </span>

      <h3
        style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontWeight: 300,
          fontStyle: "italic",
          fontSize: "clamp(3.6rem, 6.5vw, 6.5rem)",
          color: "#F2E8D8",
          lineHeight: 1.0,
          letterSpacing: "-0.012em",
          marginBottom: "1.75rem",
        }}
      >
        {item.heading}
      </h3>

      <p
        style={{
          fontFamily: '"Outfit", sans-serif',
          fontSize: "0.875rem",
          color: "rgba(242,232,216,0.35)",
          lineHeight: 1.85,
          maxWidth: "27ch",
          whiteSpace: "pre-line",
        }}
      >
        {item.body}
      </p>
    </div>
  );
}

export default function Stats() {
  const { lang } = useLang();
  const sectionRef = useRef(null);

  const items = c.principles.items.map((item) => ({
    ...item,
    heading: item.heading[lang],
    body:    item.body[lang],
  }));

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const leftY  = useSpring(useTransform(scrollYProgress, [0, 1], [18, -18]), { stiffness: 28, damping: 22 });
  const rightY = useSpring(useTransform(scrollYProgress, [0, 1], [-18, 18]), { stiffness: 28, damping: 22 });

  return (
    <section
      ref={sectionRef}
      id="zasady"
      className="relative overflow-hidden"
      style={{ background: "#090806" }}
      data-testid="stats-section"
    >
      {/* Hairline top */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 1,
          background: "linear-gradient(to right, transparent, rgba(200,121,65,0.14) 30%, rgba(200,121,65,0.14) 70%, transparent)",
        }}
      />

      {/* Ambient amber — top-left */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", pointerEvents: "none",
          top: "-5%", left: "-5%",
          width: 900, height: 900, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(200,121,65,0.026) 0%, transparent 60%)",
          filter: "blur(90px)",
        }}
      />

      {/* Ambient ice — bottom-right */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", pointerEvents: "none",
          bottom: "-5%", right: "-5%",
          width: 800, height: 800, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(124,196,216,0.018) 0%, transparent 60%)",
          filter: "blur(90px)",
        }}
      />

      <div className="max-w-7xl mx-auto" style={{ padding: "9rem 2rem 8rem" }}>

        {/* Section label */}
        <p
          className="reveal"
          style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: "0.58rem",
            letterSpacing: "0.44em",
            textTransform: "uppercase",
            color: "rgba(242,232,216,0.15)",
            marginBottom: "8rem",
          }}
        >
          {c.principles.label[lang]}
        </p>

        {/* 2 × 2 editorial grid */}
        <div className="principles-grid">

          {/* ── Left column ── */}
          <motion.div style={{ y: leftY }} className="principles-col principles-col-left">
            <div
              className="principles-cell principles-cell-border-r principles-cell-border-b"
              style={{ paddingRight: "clamp(2.5rem, 7vw, 7rem)", paddingBottom: "7rem" }}
            >
              <Principle item={items[0]} delay={1} right={false} />
            </div>
            <div
              className="principles-cell principles-cell-border-r"
              style={{ paddingRight: "clamp(2.5rem, 7vw, 7rem)", paddingTop: "7rem" }}
            >
              <Principle item={items[2]} delay={3} right={false} />
            </div>
          </motion.div>

          {/* ── Right column — editorial offset ── */}
          <motion.div style={{ y: rightY }} className="principles-col principles-col-right">
            <div
              className="principles-cell principles-cell-border-b"
              style={{
                paddingLeft: "clamp(2.5rem, 7vw, 7rem)",
                paddingTop: "clamp(4rem, 9vw, 9rem)",
                paddingBottom: "7rem",
              }}
            >
              <Principle item={items[1]} delay={2} right={true} />
            </div>
            <div
              className="principles-cell"
              style={{ paddingLeft: "clamp(2.5rem, 7vw, 7rem)", paddingTop: "7rem" }}
            >
              <Principle item={items[3]} delay={4} right={true} />
            </div>
          </motion.div>

        </div>
      </div>

      {/* Hairline bottom */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 1,
          background: "linear-gradient(to right, transparent, rgba(200,121,65,0.14) 30%, rgba(200,121,65,0.14) 70%, transparent)",
        }}
      />
    </section>
  );
}
